import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, get } from 'lodash';
import { updateSelectedMetrics, updateScanTableColumns } from '@store/viewerSessions/actions';
import dimensionsSelector from '@store/common/dimensionList/selectors';
import commonSelector from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { CDN_ACCOUNT_TYPE } from '@utils/constants';
import {
  columnsMetrics as defaultColumnsMetrics,
  getColumnsNameReflection,
  MAX_ANCHORED_COUNT,
  GROUPBY_MAPPING,
} from '../utils';
import CustomizeDropdown from './CustomizeColumns';

const compare = (columnsNameReflection) => (a, b) =>
  columnsNameReflection[a].toLowerCase() < columnsNameReflection[b].toLowerCase() ? -1 : 1;

// const isTag = dimension => dimension.field === 'TAG' || dimension.field.startsWith('m3.');

const mapDimensionGroupBy = (dimension) => {
  // if (isTag(dimension)) {
  //   return `tags.${dimension.groupBy}`;
  // }
  return get(GROUPBY_MAPPING, dimension.groupBy, dimension.groupBy);
};

const mandatoryDimensions = ['SessionId', 'ViewerId', 'SessionStartTime'];

const MetricDropMenu = ({
  selectedMetrics,
  anchoredMetrics,
  exceedTimeLimit,
  viewerDrillDownKey,
  customerAccountType,
}) => {
  const timelineFlag = useSelector(commonSelector).features[featureNames.VIEWER_DRILLDOWN_TIMELINE];
  const dispatch = useDispatch();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [allMetrics, setAllMetrics] = useState({});

  const { response: dimensions } = useSelector(dimensionsSelector);
  const columnsNameReflection = React.useMemo(
    () => ({
      ...getColumnsNameReflection(),
      ...Object.fromEntries(dimensions.map((d) => [mapDimensionGroupBy(d), d.name])),
    }),
    [dimensions],
  );

  useEffect(() => {
    const columnsMetrics = cloneDeep(defaultColumnsMetrics);
    // hide cdn dimension for cdn partner accounts
    if (customerAccountType === CDN_ACCOUNT_TYPE && columnsMetrics['Interval Playback Metrics']) {
      columnsMetrics['Interval Playback Metrics'] = columnsMetrics[
        'Interval Playback Metrics'
      ].filter((dimension) => dimension !== 'IntervalCDN');
    }
    const basics = columnsMetrics['Basic Information'];
    // Dedupe against every existing column group (not just Basic Information) so a
    // dimension that already exists in another group (e.g. EndedStatus in Lifetime
    // Playback Metrics) is not surfaced a second time.
    const existingColumns = new Set<string>(Object.values(columnsMetrics).flat() as string[]);
    columnsMetrics['Basic Information'] = [
      ...basics,
      ...dimensions
        .map((d) => mapDimensionGroupBy(d))
        .filter((d) => !existingColumns.has(d))
        .filter((d) => d !== 'IntervalCDN') // CDN is in the Interval Playback Metrics section already
        .sort(compare(columnsNameReflection)),
    ];
    const keys = Object.keys(columnsMetrics);
    const finalMetricsList = keys.map((key) => {
      const finalMetricList = columnsMetrics[key]
        .filter((v) => {
          // If user does not have timeline permission, we should hidden it on dropmenu
          if (v === 'SessionId' && !timelineFlag) {
            return false;
          }
          return true;
        })
        .map((v) => {
          return Object.assign({}, { name: columnsNameReflection[v], metric: v });
        });
      return { [key]: finalMetricList };
    });

    setAllMetrics(Object.assign({}, ...finalMetricsList));
  }, [dimensions, customerAccountType, columnsNameReflection, timelineFlag]);

  const generateNewColumns = (metricArray) =>
    metricArray.map((item) => ({
      dataKey: item,
      frozen: anchoredMetrics[item] ? 'left' : false,
      title: columnsNameReflection[item],
      width: 200,
      height: 60,
      resizable: true,
      sortable: true,
      maxWidth: 340,
      minWidth: 60,
      key: item,
    }));

  useEffect(() => {
    let tableColumns = generateNewColumns(selectedMetrics);
    // hide cdn dimension for cdn partner accounts
    if (customerAccountType === CDN_ACCOUNT_TYPE) {
      tableColumns = tableColumns.filter((tableColumn) => tableColumn.dataKey !== 'IntervalCDN');
    }
    dispatch(updateScanTableColumns(tableColumns, viewerDrillDownKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetrics, anchoredMetrics]);

  const applyColumns = (preferences) => {
    dispatch(
      updateSelectedMetrics(
        preferences.selectedMetrics,
        viewerDrillDownKey,
        preferences.anchoredMetrics,
      ),
    );
  };

  return (
    <div id="viewer-session-customize-dropdown" ref={containerRef}>
      <CustomizeDropdown
        maxAnchorCount={MAX_ANCHORED_COUNT}
        allMetrics={allMetrics}
        selectedMetrics={selectedMetrics}
        anchoredMetrics={anchoredMetrics}
        disabled={exceedTimeLimit}
        mandatoryDimensions={mandatoryDimensions}
        getPopupContainer={() => containerRef.current || document.body}
        onApply={applyColumns}
      />
    </div>
  );
};

MetricDropMenu.defaultProps = {
  selectedMetrics: [],
  anchoredMetrics: {},
  exceedTimeLimit: false,
};

export default MetricDropMenu;
